import { useMemo, useState } from "react";
import classNames from "classnames";
import { Label, Typography, BasicTable } from "components/atoms";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { RefreshModal } from "components/molecules/RefreshModal";
import { DrillModal } from "components/organisms/TabClassHomeWork";
import { decodeToken } from "utils/functions/decodeToken";
import {
  useGetCourseworkQuery,
  useGetOverallProgressQuery,
  useGetTestScoresAndPerformanceQuery,
} from "services/homeApi";
import "./appeal-reason.scss";
import { useSegmentReFetch } from "context";
import { useAppDispatch } from "hooks";
import { getObjectKeysByValue } from "services/helpers";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { replaceArrayOfObjects, reStructureSegment } from "utils/functions/courseWorkReStructure";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { DASHBOARD_PROGRAME, MCAT_TEST_TYPE, SELF_PACED_PROGRAME } from "utils/constants";
import { isMCATExternalContent, openExternalUrl } from "utils/functions/openExternalUrl";

dayjs.extend(utc);

interface TestData {
  name: string;
  dueDate: string;
  reasons: string[];
  action: string;
  launchUrl: string;
}

interface IProps {
  tableData: any;
  theme: "light" | "dark";
  onParentModalClose?: () => void;
}

export const AppealReason = ({ tableData, theme, onParentModalClose }: IProps) => {
  const dispatch = useAppDispatch();
  const [openUpdateModalState, setOpenUpdateModalState] = useState<any>({
    isOpen: false,
    data: null,
  });
  const [isRefreshModalOpen, setIsReFreshModalOpen] = useState(false);
  const [contentObjectType, setContentObjectType] = useState<
    string | undefined
  >("");

  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const testtype =
    payload && payload.hasOwnProperty("testtype") && payload.testtype;
  const isMCAT = testtype === MCAT_TEST_TYPE;
  const isAiEnabled =
    !!payload &&
    typeof payload.showaitutor === "string" &&
    payload.showaitutor.toLowerCase() === "true";

  const { refetch, isFetching } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [courseWorkPageNo, setCourseWorkPageNo] = useState(1);
  const {
    data: courseWorkData,
    setData,
    selectedBtn,
    selectedSession,
    subject
  } = useSegmentReFetch();

  const { refetch: courseWorkRefetch, isFetching: courseWorkIsFetching } =
    useGetCourseworkQuery({
      page: courseWorkPageNo,
      ...(isDashboard
        ? {
            selectedOption: parseInt(
              getObjectKeysByValue(selectedBtn, true)[0],
              10
            ),
          }
        : {}),
      ...(isDashboard
        ? {
            timeFilter:
              selectedSession === null
                ? 1
                : getTimeFilterInput(Object.values(selectedSession)[0]),
          }
        : {}),
      ...(isSelfPaced && testtype === MCAT_TEST_TYPE
        ? {
            subject: subject,
          }
        : {}),
    });

  const {
    refetch: testScoresAndPerformanceRefetch,
    isFetching: testScoresAndPerformanceIsfetching,
  } = useGetTestScoresAndPerformanceQuery();

  const [ triggerRefetchAiRecommendation, aiRecommendationRefetchResult ] = useLazyGetAiRecommendationsQuery();

  const [ triggerCourseIndexRefetch, refetchCourseIndexIsResult ] =
    useLazyGetCourseIndexQuery();

  const handleRefreshModalOpen = (contentType?: string) => {
    setContentObjectType(() => contentType || "Test");
    setIsReFreshModalOpen(() => true);
  };

  const handleRefresh = async () => {
    const promises = [
      courseWorkRefetch(),
      refetch && refetch(),
      triggerCourseIndexRefetch(),
      testScoresAndPerformanceRefetch(),
      triggerRefetchAiRecommendation()
    ]

    Promise.allSettled(promises).then((responses:any) => {
      if (responses[0].status === "fulfilled") {
        const deStructuredArray = reStructureSegment(responses[0].value.data);
        const updatedArray = replaceArrayOfObjects(
          deStructuredArray,
          courseWorkData
        );
        setData([...updatedArray]);
        handleRefreshModalClose();
      }
      handleRefreshModalClose();
    })
  };

  const extractEntriesWithStatus = (
    obj: any,
    statuses = ["Not Started", "Not Attended"]
  ) => {
    let result: any = [];

    const recursiveExtract = (data: any) => {
      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          const isValidStatus = item.Status && statuses.includes(item.Status);
          const isInPast = (dateField: any) =>
            dayjs(dateField).isValid()
              ? dayjs(dateField).isBefore(dayjs())
              : false;

          if (isValidStatus) {
            if (
              (item.Status === "Not Started" ||
                item.Status === "Not Attended") &&
              (isInPast(item.DueDate) ||
                isInPast(item.dueDate) ||
                isInPast(item.ClassDate))
            ) {
              result.push(item);
            }
          }
        });
      } else if (typeof data === "object" && data !== null) {
        Object.entries(data).forEach((value:any) => {
          if(value[0] === "Tests") {
            let dataWithSections = value[1].map((row:any) => ({...row, sectionType: value[0]}) )
            recursiveExtract(dataWithSections);
          } else if(value[0] === "Homework" || value[0] === "Diagnostics") {
            let dataWithObjects = {...value[1]};
            // TODO: recheck below logic
            // for(let object in dataWithObjects) {
            //   dataWithObjects[object].forEach((childEle:any) => {
            //     return {
            //       ...childEle,
            //       sectionType: value[0]
            //     }
            //   })
            // }
            recursiveExtract(dataWithObjects);
          } else {
            recursiveExtract(value[1]);
          }
        });
      }
    };

    recursiveExtract(obj);
    return result;
  };

  const entriesWithStatus = useMemo(() => {
    return extractEntriesWithStatus(tableData);
  }, [tableData]);

  const data: TestData[] = entriesWithStatus.map((entry: any) => {
    if (entry.hasOwnProperty("DueDate") || entry.hasOwnProperty("dueDate")) {
      const extractDueDate = entry.DueDate || entry.dueDate;
      return {
        name: entry.Name,
        dueDate: dayjs(extractDueDate).isValid()
          ? dayjs.utc(extractDueDate).format("MM/DD/YY")
          : extractDueDate,
        reasons: [entry.Status],
        Status: entry.Status,
        action: "Appeal",
        launchUrl: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.LaunchUrl
          : entry.hasOwnProperty("LaunchUrl")
            ? entry.LaunchUrl
            : "",
        ContentObjectType: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectType
          : entry.Type,
        ContentObjectGuid: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectGuid
          : "",
      };
    } else {
      return {
        name: entry.Name,
        dueDate: dayjs(entry.ClassDate).isValid()
          ? dayjs.utc(entry.ClassDate).format("MM/DD/YY")
          : entry.ClassDate,
        reasons: [entry.Status],
        Status: entry.Status,
        action: "Appeal",
        launchUrl: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.LaunchUrl
          : entry.hasOwnProperty("LaunchUrl")
            ? entry.LaunchUrl
            : "",
        ContentObjectType: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectType
          : entry.Type,
        ContentObjectGuid: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectGuid
          : "",
      };
    }
  });

  const handleClick = (obj: any) => {
    if (
      obj.ContentObjectType === "OfflineDrill" ||
      obj.ContentObjectType === "OfflineReading"
    ) {
      setOpenUpdateModalState((prev: any) => ({
        ...prev,
        isOpen: true,
        data: obj,
      }));
    } else {
      if (
        isMCAT &&
        isAiEnabled &&
        onParentModalClose &&
        !isMCATExternalContent(obj.ContentObjectType)
      ) {
        onParentModalClose();
      }
      openExternalUrl({
        url: obj.launchUrl,
        contentObjectType: obj.ContentObjectType || obj.Type,
        testType: testtype,
        dispatch,
        onRefreshModalOpen: handleRefreshModalOpen,
      });

    }
  };

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsReFreshModalOpen((prev) => false);
  };

  // const handleAppealBtnClick = () => {
  //   navigate("/appeal");
  // };

  return (
    <>
      <BasicTable
        rules="none"
        columns={[
          { title: "Name", key: "name" },
          { title: "Due Date", key: "dueDate" },
          { title: "Reason(s)", key: "reasons" },
          { title: "", key: "action" },
        ]}
        data={data}
        renderHead={(customColumns) => (
          <thead className={classNames("appeal-reason-thead")}>
            <tr>
              {customColumns.map((obj, index) => (
                <th key={index + obj.title}>
                  <Typography variant="h7Bold" label={obj.title} />
                </th>
              ))}
            </tr>
          </thead>
        )}
        renderBody={(customData) => (
          <tbody className={classNames("appeal-reason-tbody")}>
            {customData.map((obj, index) => (
              <tr
                key={index}
                className={classNames({
                  highlight: index % 2 === 0,
                })}
              >
                {Object.keys(obj).map((columnName, innerIndex) => (
                  <td
                    key={innerIndex + columnName}
                    className={classNames({
                      "test-name": columnName === "name",
                    })}
                  >
                    {columnName === "name" && (
                      <Typography
                        variant="h9Medium"
                        label={obj.name}
                        styles={
                          obj.launchUrl !== null &&
                          obj.launchUrl !== undefined &&
                          typeof obj.launchUrl === "string" &&
                          obj.launchUrl.length > 0
                            ? "name-typography-custom-style"
                            : "name-typography-default-style"
                        }
                        onClick={() =>
                          obj.launchUrl !== null &&
                          obj.launchUrl !== undefined &&
                          typeof obj.launchUrl === "string" &&
                          obj.launchUrl.length > 0
                            ? handleClick(obj)
                            : null
                        }
                      />
                    )}
                    {columnName === "dueDate" && (
                      <Typography variant="h9Book" label={obj.dueDate} />
                    )}
                    {columnName === "reasons" && (
                      <div className={classNames("reason-wrapper")}>
                        {obj.reasons.map((reason: any, reasonIndex: number) => (
                          <Label
                            key={reasonIndex}
                            backgroundColor="red"
                            label={reason}
                          />
                        ))}
                      </div>
                    )}
                    {/* {columnName === "action" && (
                      <Button
                        styles="appeal-reason-btn"
                        icon={false}
                        label="Appeal"
                        onClick={handleAppealBtnClick}
                      />
                    )} */}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      />
      <RefreshModal
        isOpen={isRefreshModalOpen}
        handleOnClose={handleRefreshModalClose}
        theme={theme}
        contentObjectType={contentObjectType}
        isFetching={
          isFetching ||
          refetchCourseIndexIsResult.isFetching ||
          testScoresAndPerformanceIsfetching ||
          courseWorkIsFetching ||
          aiRecommendationRefetchResult.isFetching
        }
        handleRefresh={handleRefresh}
      />
      <DrillModal
        handleUpdateModalClose={handleUpdateModalClose}
        openUpdateModalState={openUpdateModalState}
        theme={theme}
        isFetchingOtherAPIs={
          isFetching ||
          refetchCourseIndexIsResult.isFetching ||
          testScoresAndPerformanceIsfetching ||
          courseWorkIsFetching ||
          aiRecommendationRefetchResult.isFetching
        }
        refetchData={handleRefresh}
      />
    </>
  );
};
